/**
 * PushOptInCard — one-time opt-in UI for the daily Companion Rewards
 * push nudge. Renders inside the Rewards screen.
 *
 * Flow:
 *   1. Fetch /api/push/status → { available, subscribed }.
 *   2. If !available (VAPID keys not set in env, or non-patient), hide.
 *   3. If subscribed, show a small "You'll get daily nudges" chip.
 *   4. Otherwise show the CTA. Click:
 *        a. Ensure /companion service worker is registered.
 *        b. Call Notification.requestPermission(). If not granted, abort.
 *        c. GET /api/push/vapid-key.
 *        d. sw.pushManager.subscribe({ userVisibleOnly:true, applicationServerKey }).
 *        e. POST /api/push/subscribe with { endpoint, keys:{p256dh,auth} }.
 */
import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellRing, X } from "lucide-react";

// base64url → Uint8Array (VAPID public key needs conversion for
// pushManager.subscribe applicationServerKey).
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

async function getRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/companion");
    if (reg) return reg;
    return await navigator.serviceWorker.register("/sw.companion.js", { scope: "/companion" });
  } catch {
    return null;
  }
}

export default function PushOptInCard() {
  const [state, setState] = useState({ loading: true, available: false, subscribed: false });
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("glpflo_push_dismissed") === "true");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/push/status");
      setState({ loading: false, available: !!data.available, subscribed: !!data.subscribed });
    } catch {
      setState({ loading: false, available: false, subscribed: false });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const enable = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (typeof Notification === "undefined" || !("serviceWorker" in navigator) || !("PushManager" in window)) {
        toast.error("Your browser doesn't support push notifications.");
        return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Notifications blocked. Enable them in browser settings.");
        return;
      }
      const reg = await getRegistration();
      if (!reg) { toast.error("Couldn't register service worker."); return; }
      const keyResp = await API.get("/push/vapid-key");
      const applicationServerKey = urlBase64ToUint8Array(keyResp.data.public_key);
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
      const raw = sub.toJSON();
      await API.post("/push/subscribe", {
        endpoint: raw.endpoint,
        keys: raw.keys,
        tz_offset_minutes: -new Date().getTimezoneOffset(),
      });
      toast.success("Daily nudges enabled 🎉");
      setState((s) => ({ ...s, subscribed: true }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't enable notifications");
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const disable = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const reg = await getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        const raw = sub.toJSON();
        await API.post("/push/unsubscribe", { endpoint: raw.endpoint, keys: raw.keys });
        await sub.unsubscribe();
      }
      toast.success("Daily nudges turned off");
      setState((s) => ({ ...s, subscribed: false }));
    } catch {
      toast.error("Couldn't turn off notifications");
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem("glpflo_push_dismissed", "true");
  };

  if (state.loading || !state.available || dismissed) return null;

  if (state.subscribed) {
    return (
      <div className="rounded-2xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-3 flex items-center gap-3" data-testid="companion-push-status">
        <BellRing className="w-4 h-4 text-purple-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-slate-900 dark:text-white">Daily rewards nudges ON</p>
          <p className="text-[10px] text-slate-500 dark:text-slate-400">We&apos;ll ping you at 10 AM with your points progress.</p>
        </div>
        <button
          onClick={disable}
          disabled={busy}
          className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800"
          data-testid="companion-push-disable-btn"
        >
          Turn off
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-gradient-to-br from-fuchsia-50 to-purple-50 dark:from-fuchsia-900/20 dark:to-purple-900/20 border border-fuchsia-200 dark:border-fuchsia-800 p-3 relative" data-testid="companion-push-optin">
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 p-1 hover:bg-slate-200/50 rounded"
        aria-label="Dismiss"
        data-testid="companion-push-dismiss-btn"
      >
        <X className="w-3.5 h-3.5 text-slate-400" />
      </button>
      <div className="flex items-center gap-3 pr-6">
        <div className="w-9 h-9 rounded-xl bg-white dark:bg-slate-900 border border-fuchsia-200 flex items-center justify-center flex-shrink-0">
          <Bell className="w-4 h-4 text-fuchsia-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-slate-900 dark:text-white">Get a daily nudge</p>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-snug">&quot;You&apos;re 1,247 pts from your next reward&quot; — one gentle ping each morning.</p>
        </div>
        <button
          onClick={enable}
          disabled={busy}
          className="rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs font-bold px-3 py-1.5 whitespace-nowrap disabled:opacity-60"
          data-testid="companion-push-enable-btn"
        >
          {busy ? "…" : "Turn on"}
        </button>
      </div>
    </div>
  );
}
